import React, { useState } from 'react';

function StatusBadge({ status }) {
  const colors = {
    pending: { bg: '#fff8e6', color: '#b06a00' },
    approved: { bg: '#e6f9f0', color: '#1a8a4a' },
    submitted: { bg: '#e8f0fe', color: '#00395C' },
  };
  const s = colors[status] || colors.pending;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {status}
    </span>
  );
}

export default function Payroll({ navigateTo }) {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeclock', label: 'Time Clock' },
    { id: 'adjustments', label: 'Adjustments' },
    { id: 'reports', label: 'Reports' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Payroll</h2>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--gray-200)' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '8px 16px',
              fontSize: 12, fontWeight: 600, fontFamily: 'Montserrat',
              color: activeTab === tab.id ? 'var(--navy)' : 'var(--gray-500)',
              borderBottom: activeTab === tab.id ? '2px solid var(--orange)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'timeclock' && <TimeClockTab />}
      {activeTab === 'adjustments' && <AdjustmentsTab />}
      {activeTab === 'reports' && <ReportsTab />}
    </div>
  );
}

function OverviewTab() {
  return (
    <div>
      {/* Connection status cards */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi-card">
          <div className="kpi-label">TCP TimeClock</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ccc', display: 'inline-block' }} />
            <span style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>Not Connected</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 4 }}>API credentials needed</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">ADP Run</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ccc', display: 'inline-block' }} />
            <span style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>Not Connected</span>
          </div>
          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 4 }}>Marketplace registration needed</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Employees</div>
          <div className="kpi-value">~15-20</div>
          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 4 }}>Will sync from TCP</div>
        </div>
      </div>

      {/* Workflow overview */}
      <div className="card">
        <div className="card-header">Payroll Workflow</div>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { step: 1, title: 'Pull Hours', desc: 'Import time clock data from TCP', status: 'pending', icon: '⏱' },
              { step: 2, title: 'Review & Adjust', desc: 'PTO, sick leave, commissions', status: 'pending', icon: '✏️' },
              { step: 3, title: 'Approve', desc: 'Confirm final hours and pay', status: 'pending', icon: '✓' },
              { step: 4, title: 'Submit to ADP', desc: 'Push approved payroll to ADP Run', status: 'pending', icon: '📤' },
            ].map(s => (
              <div key={s.step} style={{
                flex: '1 1 180px', padding: '14px 16px', background: 'var(--gray-50)', borderRadius: 8,
                border: '1px solid var(--gray-200)', position: 'relative',
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', marginBottom: 4 }}>STEP {s.step}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>{s.title}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{s.desc}</div>
                <div style={{ marginTop: 8 }}><StatusBadge status={s.status} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Setup checklist */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">Setup Checklist</div>
        <div style={{ padding: 16, fontSize: 12, lineHeight: 2 }}>
          {[
            { done: false, text: 'Get TCP TimeClock Plus API credentials (contact TCP admin or support)' },
            { done: false, text: 'Register for ADP Marketplace API access' },
            { done: false, text: 'Configure TCP API connection in CPx' },
            { done: false, text: 'Map TCP employees to ADP employee records' },
            { done: false, text: 'Set up PTO/sick leave policies' },
            { done: false, text: 'Configure commission rules' },
            { done: false, text: 'Test end-to-end payroll flow' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: item.done ? '#1a8a4a' : 'var(--navy)' }}>
              <span style={{ width: 16, height: 16, borderRadius: 4, border: item.done ? '2px solid #1a8a4a' : '2px solid var(--gray-300)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#1a8a4a' }}>
                {item.done ? '✓' : ''}
              </span>
              {item.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TimeClockTab() {
  return (
    <div className="card">
      <div className="card-header">Time Clock Data</div>
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 12 }}>
          Connect TCP TimeClock Plus to pull employee hours automatically.
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 16 }}>
          Once connected, you'll see employee hours here with the ability to review,<br />
          adjust for PTO/sick leave, and approve before submitting to ADP.
        </div>
        <div style={{ display: 'inline-block', padding: '10px 20px', background: 'var(--gray-100)', borderRadius: 8, fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>
          Waiting for TCP API credentials
        </div>
      </div>
    </div>
  );
}

function AdjustmentsTab() {
  return (
    <div className="card">
      <div className="card-header">Adjustments</div>
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 12 }}>
          Manage PTO, sick leave, and commissions here.
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>
          After time clock data is imported, you can adjust individual employee records<br />
          before submitting to ADP for payroll processing.
        </div>
      </div>
    </div>
  );
}

function ReportsTab() {
  return (
    <div className="card">
      <div className="card-header">Payroll Reports</div>
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 12 }}>
          Labor cost reports and budget tracking coming soon.
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>
          Reports will include labor costs by department, overtime tracking,<br />
          budget vs. actual comparisons, and payroll history.
        </div>
      </div>
    </div>
  );
}
